import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson } from '../lib/api';

// --- Types ---

export interface DataQualityScore {
  overall: number;
  withEmail: number;
  withPhone: number;
  withCompany: number;
  totalRecords: number;
}

export interface IncompleteRecord {
  id: string;
  entityType: string;
  name: string;
  missingFields: string[];
  completeness: number;
}

export interface DataQualityReport {
  score: DataQualityScore;
  incompleteRecords: IncompleteRecord[];
  total: number;
}

export interface StandardizeResult {
  totalScanned: number;
  totalUpdated: number;
  totalSkipped: number;
  previews?: { id: string; name: string; before: string; after: string }[];
}

// --- Hooks ---

export function useDataQuality() {
  return useQuery({
    queryKey: ['data-quality'],
    queryFn: () => fetchJson<DataQualityReport>('/api/data-quality'),
  });
}

export function useStandardizePhones() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) =>
      postJson<StandardizeResult>('/api/data-quality/standardize-phones', { dryRun }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-quality'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useStandardizeEmails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) =>
      postJson<StandardizeResult>('/api/data-quality/standardize-emails', { dryRun }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-quality'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useFindIncomplete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: string[]) =>
      postJson<DataQualityReport>('/api/data-quality/find-incomplete', { fields }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-quality'] });
    },
  });
}
